import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { updateRegionBasedAPIEndpoint } from 'flow/actions/app-actions';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import {
    isBetaOrDevEnvironment,
    isEmbeddedTypeCti,
    regionCheckEndpoint,
    regionLookupEnabled,
} from 'flow/utils/app-utils';
import {
    GenericRegionType,
    PlatformType,
    RegionDataType,
} from 'flow/types/region-lookup/UserRegion';
import { ElectronAPIProps, getElectronAPI } from 'flow/utils/electron-api';
import isElectron from 'is-electron';
import { regions } from 'flow/constants/region-lookup/login-data';
import { apiUrl } from 'app-config';
import { useQuery } from '@tanstack/react-query';

import {
    fetchAllRegions,
    fetchFeatureFlagsFromGeonex,
    fetchRegionDataEndpoint,
    isAllowedUrl,
    isRegionSelectorEnabled,
    validateRegionData,
} from 'flow/utils/geonex-api-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';

interface UserRegionProviderProps {
    children: ReactNode;
}

export interface UserRegionContextProps {
    regionData?: RegionDataType;
    allRegions: GenericRegionType[] | null;
    clearRegionData: () => void;
    updateRegionData: (data: Partial<RegionDataType>) => void;
    isRegionCheckEnabled?: boolean;
    fetchRegionData: (username: string, isOidcLogin?: boolean) => void;
    isEndpointUpdated?: boolean;
    setEmbedType?: (type: string) => void;
    isRegionInitialized: boolean;
    isRegionsLoading?: boolean;
    clearUserData: () => void;
}

export const UserRegionContext = createContext<UserRegionContextProps | null>(
    null,
);

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const FEATURE_FLAG_STALE_TIME = 60 * 1000; // matches Geonex's Cache-Control: max-age=60

/**
 * Static prerequisites for region check — all read from runtime config at
 * import time, so they cannot change between renders.
 */
const REGION_CHECK_CONFIGURED =
    !IS_UAE_HOST && !!regionCheckEndpoint && regionLookupEnabled;

/**
 * Whether boot has to wait for region state to settle. Only Electron does:
 * Main dispatches prepareInitialState exactly once off isRegionInitialized and
 * picks the session endpoint from it, and Electron is the one platform we can
 * recognise synchronously. CTI only learns it is CTI once EmbedApplication
 * reports the embed type, which is after this provider's first render, so it
 * has never gated boot — and an ordinary browser session never can, so it must
 * not start waiting on a Geonex round-trip to find that out.
 */
const REGION_CHECK_GATES_BOOT = REGION_CHECK_CONFIGURED && isElectron();

/**
 * Which platforms region check applies to, unchanged from before the Geonex
 * feature flag existed: CTI on beta/dev, or Electron. The flag is layered on
 * top of this as an extra gate, never as a replacement — so only sessions that
 * were already eligible ask Geonex for it.
 *
 * Enables Region Lookup temporarily for CTI logins in beta and development
 * environments for testing purposes. isBetaOrDevEnvironment() check will be
 * removed after testing.
 */
const isRegionCheckPlatform = (isCtiEmbedded: boolean) =>
    REGION_CHECK_CONFIGURED &&
    ((isCtiEmbedded && isBetaOrDevEnvironment()) || isElectron());

const UserRegionProvider: FC<UserRegionProviderProps> = ({ children }) => {
    const [regionData, setRegionData] = useState<RegionDataType>();
    const regionDataRef = useRef(regionData);
    regionDataRef.current = regionData;
    const [electronAPIInstance, setElectronAPIInstance] =
        useState<ElectronAPIProps | null>(null);
    const [userNameToFetch, setUserNameToFetch] = useState<string | null>(null);
    const [isEndpointUpdated, setIsEndpointUpdated] = useState(false);
    const [embedType, setEmbedType] = useState<PlatformType | null>(null);
    const appliedUrlRef = useRef<string | null>(null);
    const isCtiEmbedded = embedType ? isEmbeddedTypeCti(embedType) : false;

    /**
     * Geonex publishes the pre-login feature flags every client reads before
     * authenticating. Asked for only by the platforms that can act on the
     * answer — on Electron that is from mount, on CTI as soon as the embed type
     * arrives — and never retried: a failed or malformed response reads as
     * "disabled", which is the safe direction.
     */
    const { data: featureFlags, isFetched: areFeatureFlagsFetched } = useQuery({
        queryKey: ['geonexFeatureFlags'],
        queryFn: fetchFeatureFlagsFromGeonex,
        enabled: isRegionCheckPlatform(isCtiEmbedded),
        staleTime: FEATURE_FLAG_STALE_TIME,
        gcTime: FEATURE_FLAG_STALE_TIME,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    /**
     * The platform rule above, plus Geonex's say-so as an extra gate.
     */
    const isRegionCheckEnabledInitial = useMemo(
        () =>
            isRegionCheckPlatform(isCtiEmbedded) &&
            isRegionSelectorEnabled(featureFlags),
        [isCtiEmbedded, featureFlags],
    );

    /**
     * Determines whether region check is enabled.
     * Region check is enabled only when the environment variable is set,
     * Geonex says the region selector is on, and the application is running
     * in CTI embedded or Electron mode.
     *
     * Where boot waits on this (see REGION_CHECK_GATES_BOOT), it must not start
     * before the flags have settled: Main dispatches prepareInitialState
     * exactly once off isRegionInitialized, and uses isRegionCheckEnabled to
     * pick the initial session endpoint.
     */
    const [isRegionInitialized, setIsRegionInitialized] = useState(
        !REGION_CHECK_GATES_BOOT,
    );

    const {
        data: fetchedRegionData,
        error,
        refetch,
    } = useQuery({
        queryKey: ['regionData', userNameToFetch],
        queryFn: () => fetchRegionDataEndpoint(userNameToFetch!),
        enabled: !!userNameToFetch,
        retry: 1,
    });

    const {
        data: allRegions = null,
        error: errorRegions,
        isLoading: isRegionsLoading,
    } = useQuery({
        queryKey: ['allRegions'],
        queryFn: fetchAllRegions,
        enabled: isRegionCheckEnabledInitial,
        staleTime: STALE_TIME,
        retry: 1,
        gcTime: STALE_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const isRegionCheckEnabled = isRegionCheckEnabledInitial && !errorRegions;

    const updateRegionData = useCallback(
        (data: Partial<RegionDataType>) => {
            setRegionData(
                (prevData) =>
                    ({
                        ...prevData,
                        ...data,
                    }) as RegionDataType,
            );
        },
        [setRegionData],
    );

    useEffect(() => {
        if (fetchedRegionData && userNameToFetch) {
            if (fetchedRegionData.url && !isAllowedUrl(fetchedRegionData.url)) {
                console.error(
                    'Geonex returned a disallowed URL, ignoring response:',
                    fetchedRegionData.url,
                );
                return;
            }

            setRegionData(
                (prevData) =>
                    ({
                        ...prevData,
                        ...fetchedRegionData,
                        userName: userNameToFetch,
                    }) as RegionDataType,
            );
        }
    }, [fetchedRegionData, userNameToFetch]);

    useEffect(() => {
        if (error && userNameToFetch) {
            setRegionData({
                region: regions.SWEDEN,
                url: apiUrl,
                userName: userNameToFetch,
                lastUpdatedTime: null,
                isOidcLogin: false,
            });
        }
    }, [error, userNameToFetch]);

    const clearRegionData = useCallback(() => {
        setRegionData(undefined);
        setUserNameToFetch(null);
        setIsEndpointUpdated(false);
        appliedUrlRef.current = null;
        if (isElectron()) {
            electronAPIInstance?.removeUserRegionData();
        } else {
            wrappedLocalStorage.removeItem('regionData');
        }
    }, [electronAPIInstance]);

    const clearUserData = useCallback(() => {
        if (regionDataRef.current?.socialLoginType) {
            const clearData = {
                userName: undefined,
                isOidcLogin: undefined,
                socialLoginType: undefined,
                socialSelectedUrl: undefined,
                successLoginCompleted: undefined,
            };
            updateRegionData(clearData);
        } else {
            clearRegionData();
        }
    }, [updateRegionData, clearRegionData]);

    const fetchRegionData = useCallback(
        (userName: string, isOidcLogin = false) => {
            if (userName) {
                setIsEndpointUpdated(false);
                appliedUrlRef.current = regionDataRef.current?.url || null;
                setUserNameToFetch(userName.trim());
                updateRegionData({
                    isOidcLogin,
                    successLoginCompleted: false,
                    lastUpdatedTime: null,
                });
                if (!regionDataRef.current?.successLoginCompleted) {
                    refetch();
                }
            }
        },
        [refetch, updateRegionData],
    );

    useEffect(() => {
        if (!isRegionCheckEnabledInitial) {
            // Where boot waits on this effect, release it only once the flags
            // have settled — unblocking earlier would let prepareInitialState
            // run against the wrong session endpoint. Everywhere else this is
            // a no-op: isRegionInitialized already started true.
            if (areFeatureFlagsFetched) {
                setIsRegionInitialized(true);
            }
            return;
        }

        if (isElectron()) {
            const electronAPI = getElectronAPI();
            setElectronAPIInstance(electronAPI!);

            const getRegionDataFromElectron = async () => {
                const rawData = await electronAPI?.getUserRegionData();
                const validData = validateRegionData(rawData);

                if (validData) {
                    setRegionData(validData);
                }

                setIsRegionInitialized(true);
            };

            getRegionDataFromElectron();
        } else {
            const storedRegionData = wrappedLocalStorage.getItem('regionData');
            if (storedRegionData) {
                try {
                    const parsedRegionData = validateRegionData(
                        JSON.parse(storedRegionData),
                    );
                    if (parsedRegionData) {
                        setRegionData(parsedRegionData);
                    }
                } catch {
                    wrappedLocalStorage.removeItem('regionData');
                }
            }
            setIsRegionInitialized(true);
        }
    }, [areFeatureFlagsFetched, isRegionCheckEnabledInitial]);

    const regionUrl = regionData?.url;

    useLayoutEffect(() => {
        if (!regionUrl) return;

        if (regionUrl !== appliedUrlRef.current) {
            appliedUrlRef.current = regionUrl;
            updateRegionBasedAPIEndpoint(regionData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionUrl]);

    useEffect(() => {
        if (!regionData) return;

        if (!regionData.url) return;

        if (
            regionData.url &&
            !regionData.userName &&
            !regionData.socialLoginType
        ) {
            return;
        }

        const { userName: _userName, ...regionDataWithoutUserName } =
            regionData;

        if (isElectron()) {
            electronAPIInstance?.saveUserRegionData(regionDataWithoutUserName);
        } else {
            wrappedLocalStorage.setItem(
                'regionData',
                JSON.stringify(regionDataWithoutUserName),
            );
        }

        if (regionData.url && appliedUrlRef.current === regionData.url) {
            setIsEndpointUpdated(true);
        }
    }, [regionData, electronAPIInstance, clearRegionData]);

    const userRegionContextData = useMemo(
        () => ({
            regionData,
            allRegions,
            isRegionCheckEnabled,
            clearRegionData,
            fetchRegionData,
            updateRegionData,
            isEndpointUpdated,
            setEmbedType,
            isRegionInitialized,
            isRegionsLoading,
            clearUserData,
        }),
        [
            regionData,
            allRegions,
            isRegionCheckEnabled,
            clearRegionData,
            fetchRegionData,
            updateRegionData,
            isEndpointUpdated,
            isRegionInitialized,
            isRegionsLoading,
            clearUserData,
        ],
    );

    return (
        <UserRegionContext.Provider value={userRegionContextData}>
            {children}
        </UserRegionContext.Provider>
    );
};

export default UserRegionProvider;
